import { IS_PREVIEW } from "../lib/theme";
import React, { useEffect, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { BellRing, X } from "lucide-react";
import { getRoadmap } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { formatDate } from "../lib/date";

const KEY = (id) => `rm_alert_seen_${id}`;
const HOUR = 3600 * 1000;
const DAY = 24 * HOUR;

const parseDate = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ""); return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null; };
const daysUntil = (s) => { const d = parseDate(s); if (!d) return null; return Math.ceil((d.getTime() - Date.now()) / DAY); };

// App-wide roadmap reminder: shows a modal for any alert-enabled item that is
// due. Cadence: every 24h since last dismissed; every 3h within 3 days of due.
export default function RoadmapAlerts() {
    const { user } = useAuth();
    const { data: items = [] } = useQuery({
        queryKey: ["roadmap-alerts"],
        queryFn: () => getRoadmap(),
        enabled: !!user && !IS_PREVIEW,
        refetchInterval: 10 * 60 * 1000,
        staleTime: 5 * 60 * 1000,
    });
    const [due, setDue] = useState([]);
    const [open, setOpen] = useState(false);

    const check = useCallback(() => {
        const now = Date.now();
        const list = (items || []).filter((it) => it.alert_enabled && it.alert_date).filter((it) => {
            const du = daysUntil(it.alert_date);
            if (du == null || du > 21) return false;   // only surface alerts within 21 days (overdue included)
            const intervalMs = (du <= 3) ? 3 * HOUR : DAY;
            const seen = Number(localStorage.getItem(KEY(it.id)) || 0);
            return !seen || (now - seen) >= intervalMs;
        });
        if (list.length) { setDue(list); setOpen(true); }
    }, [items]);

    useEffect(() => {
        if (!user) return;
        check();
        const t = setInterval(check, 5 * 60 * 1000);
        return () => clearInterval(t);
    }, [user, check]);

    const dismiss = () => { const now = Date.now(); due.forEach((it) => localStorage.setItem(KEY(it.id), String(now))); setOpen(false); setDue([]); };

    if (!open || !due.length) return null;
    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="roadmap-alert-modal">
            <div className="w-full max-w-md rounded-2xl border border-[#F59E0B]/40 bg-[#11141F] shadow-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10 bg-[#F59E0B]/10">
                    <BellRing className="w-4 h-4 text-[#FBBF24]" />
                    <h3 className="text-sm font-black uppercase tracking-wider text-white flex-1">Roadmap reminder{due.length > 1 ? "s" : ""}</h3>
                    <button onClick={dismiss} className="text-white/50 hover:text-white" data-testid="roadmap-alert-close"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
                    {due.map((it) => {
                        const du = daysUntil(it.alert_date);
                        const overdue = du != null && du < 0;
                        return (
                            <div key={it.id} data-testid={`roadmap-alert-item-${it.id}`}>
                                <div className="flex items-center gap-2 mb-1">
                                    <h4 className="text-base font-extrabold text-white">{it.title}</h4>
                                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${overdue ? "bg-red-500/20 text-red-300" : "bg-[#F59E0B]/15 text-[#FBBF24]"}`}>
                                        {overdue ? "Overdue" : du === 0 ? "Due today" : `${du} day${du === 1 ? "" : "s"} left`}
                                    </span>
                                </div>
                                <p className="text-xs text-white/50 mb-1">Due {formatDate(it.alert_date, { comma: true })}</p>
                                {it.info && <p className="text-sm text-white/70 whitespace-pre-line">{it.info}</p>}
                            </div>
                        );
                    })}
                </div>
                <div className="px-5 py-3 border-t border-white/10 flex justify-end">
                    <button onClick={dismiss} data-testid="roadmap-alert-ack" className="px-4 py-2 rounded-md bg-[#9D4CDD] hover:bg-[#B366F0] text-white font-bold uppercase tracking-wider text-xs">Got it</button>
                </div>
            </div>
        </div>
    );
}
